import React from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';

const PAGE_ENRICHMENTS = {
  cloud500: {
    applications: ['Plateformes offshore', 'Sites miniers', 'Chantiers de construction', 'Bases rurales', 'Camps militaires'],
    keyFeatures: [
      'Production optimale à 30°C et 80% HR',
      'Système de filtration en 6 étapes avec RO 0.5nm',
      'Connectivité IoT/WiFi pour monitoring distant',
      'Contrôle PLC intelligent avec capteurs multiples',
      'Économies d\'énergie 40-50% vs modèles traditionnels',
      'Installation mobile avec système de levage'
    ]
  },
  cloud1000: {
    applications: ['Bases militaires', 'Navires et plateformes', 'Complexes industriels', 'Hôpitaux de campagne', 'Zones rurales isolées'],
    keyFeatures: [
      'Production haute capacité 1000L+ par jour',
      'Système triple redondance pour fiabilité maximale',
      'PLC microcontrôleur avec détection multiple',
      '4 compresseurs pour performance continue',
      'Connectivité LAN et WiFi avec backend IoT',
      'Installation permanente avec réservoir externe'
    ]
  },
  cloud2000: {
    applications: ['Grandes usines', 'Centres de données', 'Complexes hôteliers', 'Hôpitaux', 'Universités', 'Centres commerciaux'],
    keyFeatures: [
      'Production industrielle 2000L par jour',
      'Architecture modulaire évolutive',
      'Maintenance prédictive IoT avancée',
      'Système de redondance N+1',
      'Contrôle SCADA intégré',
      'Installation containerisée disponible'
    ]
  },
  cloud5000: {
    applications: ['Raffineries', 'Ports industriels', 'Bases aériennes', 'Zones urbaines', 'Camps de réfugiés', 'Missions humanitaires'],
    keyFeatures: [
      'Production massive 5000L par jour',
      'Intelligence artificielle pour optimisation',
      'Architecture multi-container redondante',
      'Système de récupération d\'énergie avancé',
      'Télémaintenance satellite disponible',
      'Certification ISO 9001 et ISO 14001'
    ]
  }
};

const GenerateursIndustriels = () => {
  const { products, loading, error } = useProducts();

  const industrialGenerators = products
    .filter(p => p.category === 'industriel')
    .map(p => ({
      ...p,
      ...PAGE_ENRICHMENTS[p.id]
    }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat text-white py-20"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/5953729/pexels-photo-5953729.jpeg?auto=compress&cs=tinysrgb&w=1920')"
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-blue-200 hover:text-white">Accueil</Link>
              </li>
              <li>
                <svg className="flex-shrink-0 h-5 w-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
              </li>
              <li>
                <Link to="/produits" className="text-blue-200 hover:text-white">Produits</Link>
              </li>
              <li>
                <svg className="flex-shrink-0 h-5 w-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
              </li>
              <li>
                <span className="text-white font-medium">Générateurs Industriels</span>
              </li>
            </ol>
          </nav>

          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 drop-shadow-lg">
              Générateurs Industriels ECOCLIMA
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 drop-shadow-md">
              Solutions haute capacité de 500L à 5000L par jour pour installations industrielles
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">500L - 5000L/jour</span>
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">Connectivité IoT & IA</span>
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">Installation sur mesure</span>
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">{industrialGenerators.length} modèles industriels</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Introduction */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Solutions industrielles haute performance
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Nos générateurs industriels ECOCLIMA couvrent tous les besoins en eau pure : 
            de 500L pour les sites isolés jusqu'à 5000L pour les méga-installations. 
            Avec des technologies avancées (IoT, IA, SCADA), ils garantissent un approvisionnement 
            en eau pure constant et fiable pour les environnements les plus exigeants.
          </p>
        </div>

        {/* Generators List */}
        <div className="space-y-16">
          {industrialGenerators.map((generator, index) => (
            <div key={generator.id} data-testid={`industrial-card-${generator.id}`} className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              
              {/* Image */}
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <img
                    src={generator.image_url}
                    alt={generator.name}
                    className="w-full h-64 object-contain mx-auto"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-1/2">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{generator.name}</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-blue-600">{generator.capacity_liters}L/jour</span>
                    <span className="text-2xl font-bold text-green-600">Sur devis</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">{generator.description}</p>

                {/* Applications */}
                {generator.applications && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Applications typiques :</h4>
                    <div className="flex flex-wrap gap-2">
                      {generator.applications.map((app) => (
                        <span key={app} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Features */}
                {generator.keyFeatures && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Caractéristiques clés :</h4>
                    <ul className="space-y-1">
                      {generator.keyFeatures.slice(0, 4).map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/produits/${generator.id}`}
                    data-testid={`industrial-details-${generator.id}`}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium text-center transition-colors"
                  >
                    Voir les détails techniques
                  </Link>
                  <Link
                    to={`/devis?product=${generator.id}`}
                    data-testid={`industrial-quote-${generator.id}`}
                    className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-lg font-medium text-center transition-colors"
                  >
                    Demander un devis
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Comparaison des modèles industriels
          </h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caractéristique</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLOUD500</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLOUD1000+</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLOUD2000</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLOUD5000</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Production quotidienne</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">500L/jour</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">1000L+/jour</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">2000L/jour</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">5000L/jour</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Consommation</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">4300W<br/>(206 Wh/L)</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">8700W<br/>(210 Wh/L)</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">16500W<br/>(198 Wh/L)</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">38000W<br/>(182 Wh/L)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Alimentation</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">220V/110V<br/>monophasé</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">380V/460V<br/>triphasé</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">380V/400V<br/>triphasé</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">380V/480V<br/>triphasé industriel</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Poids</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">350 kg</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">500+ kg</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">850 kg</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">1800 kg</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Compresseurs</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">2x Vortex<br/>hermétiques</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">2x Vortex +<br/>4 ventilateurs</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">4x Vortex<br/>industriels</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">8x Compresseurs<br/>industriels</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Connectivité</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Wi-Fi<br/>IoT cloud</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">LAN, WiFi<br/>IoT backend</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Ethernet, 4G/5G<br/>SCADA, IoT</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Fiber, 5G, satellite<br/>AI monitoring</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Installation</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Mobile avec<br/>levage</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Permanente +<br/>réservoir</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Modulaire<br/>containerisée</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Multi-container<br/>redondante</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">Applications type</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Sites isolés<br/>Chantiers</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Bases militaires<br/>Navires</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Usines<br/>Hôpitaux</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">Raffineries<br/>Villes</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-20 bg-blue-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Besoin d'une solution sur mesure ?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Nos générateurs industriels peuvent être adaptés à vos besoins spécifiques. 
            Contactez notre équipe technique pour une étude personnalisée de votre projet.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Nous contacter
            </Link>
            <a
              href="tel:+590590197070"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              0590 19 70 70
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateursIndustriels;
